import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useInlineNotifications } from '../../../../hooks'
import { EpostCell } from '../../../Epost'
import { getDocFlowSendingEpostSettingsWidgetNotifications } from '../../DocFlowWidgets'

export function useDocFlowEpostSettingsNotifications({
  isActiveWidget,
  noEpostSettings,
  onOpenSupportData,
}) {
  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveNotification,
    notifications,
  } = useInlineNotifications()

  const { t } = useTranslation()

  const widgetNotifications = getDocFlowSendingEpostSettingsWidgetNotifications(
    {
      t,
      onOpenSupportData,
    }
  )

  function handleRemoveAdditionalNotifications() {
    for (const value of Object.values(widgetNotifications)) {
      handleRemoveNotification(value.id)
    }
  }

  useEffect(() => {
    if (isActiveWidget) {
      if (noEpostSettings) {
        handleRemoveAdditionalNotifications()
        handlePrependNotification(widgetNotifications.unavailable)
      } else {
        handleRemoveNotification(widgetNotifications.unavailable.id)
      }
    }
  }, [isActiveWidget, noEpostSettings])

  useEffect(() => {
    if (!isActiveWidget && notifications.length > 0) {
      handleRemoveAdditionalNotifications()
    }
  }, [isActiveWidget])

  function setSuccessNotifications(epostData, enoughCredits) {
    const successNotifications = [
      widgetNotifications.success,
      widgetNotifications.validInfo,
    ]

    if (
      epostData[EpostCell.SendToCalculateAt] &&
      epostData[EpostCell.ValidationErrors].length === 0 &&
      enoughCredits
    ) {
      handleRemoveAdditionalNotifications()

      for (const item of successNotifications) {
        handleAddNotification(item)
      }
    }
  }

  function setInfoNotification(epostData) {
    if (
      !epostData[EpostCell.SendToCalculateAt] &&
      epostData[EpostCell.ValidationErrors].length === 0 &&
      !noEpostSettings
    ) {
      handlePrependNotification(widgetNotifications.info)
    }
  }

  function setCostIsOutdatedNotification(epostData) {
    if (epostData[EpostCell.CostIsOutdated]) {
      handleRemoveAdditionalNotifications()
      handlePrependNotification(widgetNotifications.costIsOutdated)
    }
  }

  function setValidationErrorsNotification(epostData) {
    if (epostData[EpostCell.ValidationErrors].length > 0) {
      handleRemoveAdditionalNotifications()
      handleAddNotification(widgetNotifications.validationErrors)
    }
  }

  function setNotEnoughCreditsNotification(withLink) {
    const notEnoughCreditsNotification = withLink
      ? 'notEnoughCreditsWithLink'
      : 'notEnoughCredits'

    handleRemoveAdditionalNotifications()
    handlePrependNotification(widgetNotifications[notEnoughCreditsNotification])
  }

  function setNotificationsByGettingSettings(epostData, enoughCredits) {
    if (epostData[EpostCell.CostIsOutdated]) {
      setCostIsOutdatedNotification(epostData)

      return
    }

    if (epostData[EpostCell.ValidationErrors].length > 0) {
      setValidationErrorsNotification(epostData)

      return
    }

    if (enoughCredits) {
      setSuccessNotifications(epostData, enoughCredits)
    }
  }

  return {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveAdditionalNotifications,
    handleRemoveNotification,
    notifications,
    setInfoNotification,
    setNotEnoughCreditsNotification,
    setNotificationsByGettingSettings,
    setValidationErrorsNotification,
    widgetNotifications,
  }
}
